/**
 * TRKM-2740 — CNPJ alfanumérico (IN RFB 2.229/2024 + NT Conjunta 2025.001).
 *
 * Módulo canônico de documento do web-agenciador. Substitui o uso direto de
 * `cpf_cnpj@0.2.0`, que só conhece CNPJ numérico, e concentra num lugar só a máscara,
 * a normalização e a validação — para que uma tela nova não reintroduza o formato antigo.
 *
 * O CNPJ passou a ter as 12 primeiras posições alfanuméricas e os 2 dígitos verificadores
 * numéricos: `[A-Z0-9]{12}[0-9]{2}`. O CNPJ numérico é um caso particular desse formato,
 * então nada do que funciona hoje deixa de funcionar.
 *
 * ## Por que aqui a validação é local, e não a lib `cpf-cnpj-validator`
 *
 * Os demais fronts usam aquela lib. Este projeto **não a comporta sem mexer no
 * toolchain**: ela é publicada em ES2020 e usa `??`, que o acorn 5 do webpack 3 e o
 * uglify-es 3.3 não sabem ler, e que o babel 6 do projeto nem consegue parsear. Usá-la
 * exigiria instalar babel 7 em paralelo ao babel 6 e adicionar uma regra de webpack —
 * três dependências novas e uma alteração de build num projeto legado que hoje compila,
 * tudo por um algoritmo de ~25 linhas.
 *
 * A API pública deste módulo é idêntica à dos outros fronts, então o código das telas é o
 * mesmo. O cálculo do DV segue a mesma regra da NT (módulo 11 com conversão `ASCII − 48`),
 * conferido contra o CNPJ oficial de teste da SVRS `PC3D315K000193`.
 */

/**
 * Máscara de CNPJ.
 *
 * Este projeto usa `v-mask` (registrado em `main.js` via `Vue.use(VueMask)`). Na tabela
 * de tokens da lib (`node_modules/v-mask/dist/v-mask.esm.js`, `defaultMaskReplacers`) o
 * token alfanumérico é `N` (`/[a-z0-9]/i`) e `#` é só dígito (`/\d/`). Atenção: ali `A`
 * é SÓ letra, e o token alfanumérico do `vanilla-masker` é `S` — não confundir.
 */
export const MASCARA_CNPJ = 'NN.NNN.NNN/NNNN-##'

/** Máscara de CPF — segue 100% numérica. */
export const MASCARA_CPF = '###.###.###-##'

/*
 * Não existe aqui uma máscara dinâmica de CPF/CNPJ, de propósito: o `v-mask` não tem esse
 * recurso. O `bind` da diretiva chama `stringMaskToRegExpMask(value)`, que faz `value.split('')`
 * — passar um array (o formato aceito pelo `vue-the-mask`, que é outra lib) estoura
 * `TypeError: stringMask.split is not a function` e o campo renderiza sem máscara nenhuma.
 *
 * Num campo que aceite os dois, escolha a máscara numa `computed` pelo tamanho do valor já
 * normalizado e passe a string resultante para o `v-mask`.
 */

/** Formato do CNPJ sem máscara, conforme a NT. */
export const REGEX_CNPJ = /^[A-Z0-9]{12}[0-9]{2}$/

/** Formato do CPF sem máscara. */
export const REGEX_CPF = /^[0-9]{11}$/

/** Separadores da máscara. Diferente de `\D`, não descarta as letras do documento. */
const SEPARADORES_MASCARA = /[.\-/\s]/g

/** Documento com todos os caracteres iguais — inválido em qualquer formato. */
const CARACTERES_REPETIDOS = /^(.)\1+$/

/**
 * CPF que passa no cálculo do DV mas é reservado e precisa ser recusado.
 *
 * Paridade deliberada com o comportamento anterior: tanto a `cpf_cnpj` (usada até aqui)
 * quanto a `cpf-cnpj-validator` (usada nos demais fronts) mantêm exatamente esta exceção
 * além dos repetidos. Sem ela, esta implementação seria mais permissiva que a anterior.
 */
const CPF_RESERVADO = '12345678909'

/**
 * Remove a máscara e eleva a caixa, preservando as letras.
 *
 * Substitui o `replace(/\D/g, '')` / `replace(/[^0-9]/g, '')` aplicado a CNPJ: aquele
 * descarta as letras e chega a transformar um CNPJ de 14 posições em algo do tamanho de
 * um CPF — `'PC3D315K000193'` virava `'3315000193'`, com 10 posições.
 *
 * @param {string|null|undefined} documento CPF ou CNPJ, com ou sem máscara
 * @returns {string} documento sem separadores e em maiúsculas ('' quando não há valor)
 */
export function normalizaDocumento(documento) {
  if (documento === null || documento === undefined) return ''
  return String(documento).replace(SEPARADORES_MASCARA, '').toUpperCase()
}

/**
 * Calcula um dígito verificador pelo módulo 11.
 *
 * A NT define a conversão do caractere pelo seu código ASCII menos 48: dígito continua
 * valendo seu próprio valor (0-9) e letra passa a valer 17 (`A`) até 42 (`Z`).
 */
function digitoVerificador(base) {
  let soma = 0
  let peso = 2
  for (let i = base.length - 1; i >= 0; i--) {
    soma += (base.charCodeAt(i) - 48) * peso
    peso = peso < 9 ? peso + 1 : 2
  }
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/**
 * @param {string} value CNPJ com ou sem máscara
 * @returns {boolean} true quando o CNPJ é válido, inclusive no formato alfanumérico
 */
export function isCnpj(value) {
  const documento = normalizaDocumento(value)

  if (!REGEX_CNPJ.test(documento)) return false
  if (CARACTERES_REPETIDOS.test(documento)) return false

  const base = documento.substring(0, 12)
  const digitos = documento.substring(12)

  return (
    digitoVerificador(base) === Number(digitos.charAt(0)) &&
    digitoVerificador(base + digitos.charAt(0)) === Number(digitos.charAt(1))
  )
}

/**
 * Dígito verificador do CPF.
 *
 * O CPF usa pesos **decrescentes** a partir de `base.length + 1` (10..2 no primeiro
 * dígito, 11..2 no segundo) — esquema diferente do módulo 11 cíclico do CNPJ, por isso
 * não dá para reaproveitar {@link digitoVerificador} aqui.
 */
function digitoVerificadorCpf(base) {
  let soma = 0
  let peso = base.length + 1
  for (let i = 0; i < base.length; i++) {
    soma += Number(base.charAt(i)) * peso
    peso--
  }
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/**
 * @param {string} value CPF com ou sem máscara
 */
export function isCpf(value) {
  const documento = normalizaDocumento(value)

  if (!REGEX_CPF.test(documento)) return false
  if (CARACTERES_REPETIDOS.test(documento)) return false
  if (documento === CPF_RESERVADO) return false

  const primeiro = digitoVerificadorCpf(documento.substring(0, 9))
  const segundo = digitoVerificadorCpf(documento.substring(0, 10))

  return (
    primeiro === Number(documento.charAt(9)) &&
    segundo === Number(documento.charAt(10))
  )
}

/**
 * Decide entre CPF e CNPJ pelo tamanho do documento **já normalizado** — medir o valor
 * mascarado faria um CPF de 14 caracteres (`123.456.789-09`) cair no ramo CNPJ.
 */
export function isCpfCnpj(value) {
  const documento = normalizaDocumento(value)
  return documento.length > 11 ? isCnpj(documento) : isCpf(documento)
}

/**
 * Formata como `AA.AAA.AAA/AAAA-99`, aceitando numérico e alfanumérico.
 * Devolve a entrada normalizada quando não tiver 14 posições, em vez de mascarar errado.
 */
export function formataCnpj(value) {
  const documento = normalizaDocumento(value)
  if (documento.length !== 14) return documento
  return (
    documento.substring(0, 2) + '.' +
    documento.substring(2, 5) + '.' +
    documento.substring(5, 8) + '/' +
    documento.substring(8, 12) + '-' +
    documento.substring(12, 14)
  )
}

export function formataCpf(value) {
  const documento = normalizaDocumento(value)
  if (documento.length !== 11) return documento
  return (
    documento.substring(0, 3) + '.' +
    documento.substring(3, 6) + '.' +
    documento.substring(6, 9) + '-' +
    documento.substring(9, 11)
  )
}

export function formataCpfCnpj(value) {
  const documento = normalizaDocumento(value)
  return documento.length > 11 ? formataCnpj(documento) : formataCpf(documento)
}



// WEBPACK FOOTER //
// ./src/assets/js/documento.js